import { apiClient } from './client';

export type ExperimentOwner = 'platform' | 'merchant';
export type ExperimentStatus = 'draft' | 'running' | 'paused' | 'concluded';
/** How the per-segment traffic split is set: a fixed authored A/B split, or the ML optimizer per shop. */
export type OptimizationMode = 'manual' | 'ml_optimized';

export interface ExperimentVariantSummary {
  id: string;
  key: string;
  name: string;
  weight: number;
  is_control: boolean;
  payload: Record<string, unknown> | null;
}

export interface ExperimentMetricSummary {
  id: string;
  key: string;
  name: string;
  type: 'primary' | 'secondary' | 'guardrail';
  type_label: string;
  goal: 'increase' | 'decrease';
  goal_label: string;
}

export type MetricType = 'primary' | 'secondary' | 'guardrail';
export type MetricGoal = 'increase' | 'decrease';
export type ResultsReadout = 'frequentist' | 'bayesian';
export type ScorecardStatus = 'ready' | 'pending' | 'unavailable';

/**
 * `MetricResult` (the results payload) carries only a raw `type`/`goal` string — unlike
 * `ExperimentMetricSummary`, Core does not serialize a display label for it. Both the metric
 * editor and the results view need one, so it lives here once rather than as two copies that can
 * drift out of sync.
 */
export const METRIC_TYPE_LABELS: Record<MetricType, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  guardrail: 'Guardrail',
};

/**
 * Mirrors `MetricDefinition::DENOMINATOR_SOURCE_EXPOSURES` (loop-returns-app). `denominator_source`
 * reads this literal when a metric's rate is over the plain unique-exposure count; any other value
 * is another metric's `metric_key` supplying the denominator instead.
 */
export const DENOMINATOR_SOURCE_EXPOSURES = 'exposures';

/**
 * The CO+ cart-abandonment secondary metric (loop-returns-app `CheckoutPlusConversionRateTemplate`).
 * Core reports the conversion side of this rate; a consumer displays 1 minus it as the
 * abandonment rate. By design its `denominator_source` reads {@link DENOMINATOR_SOURCE_EXPOSURES}
 * — unlike the primary checkout-conversion metric, Core's template never assigns it the
 * checkout-reached override — but that is a design expectation, not something this file can
 * verify; a consumer that reads this metric guards against it being violated at runtime.
 */
export const CART_ABANDONMENT_METRIC_KEY = 'cart-abandonment-conversion';

/**
 * The CO+ "offer accepted" secondary metric (loop-returns-app `CheckoutPlusMetrics::OFFER_ACCEPTED`):
 * the shopper accepted the Checkout+ offer at checkout. Its rate is over unique exposures.
 */
export const OFFER_ACCEPTED_METRIC_KEY = 'offer-accepted';

/** Mirrors loop-returns-app `DenominatorFallbackReason`: why a declared rate denominator is not in use. */
export type DenominatorFallbackReason =
  | 'denominator_metric_missing'
  | 'arm_without_denominator_count'
  | 'paused_arm'
  | 'ml_optimized';

export type ParameterFieldType = 'enum' | 'boolean' | 'text' | 'number' | 'shopify_variant' | 'currency';
export type ParameterEditableBy = 'staff' | 'merchant';

/**
 * Declarative cross-field dependency: the owning field/option only applies when another field holds
 * one of the listed values. Shared by template parameter fields and the experiment-level owner→unit
 * relationship, so the UI evaluates both with one helper and Core validates against the same shape.
 */
export interface AvailableWhen {
  field: string;
  in: Array<string | number | boolean>;
}

/**
 * The single evaluator both the template-parameter form and the owner→unit filter use: a field/option
 * applies when it has no `availableWhen`, or the controlling field currently holds one of the values.
 */
export function matchesAvailableWhen (
  condition: AvailableWhen | null | undefined,
  values: Record<string, unknown>,
): boolean {
  if (!condition) {
    return true;
  }

  return condition.in.includes(values[condition.field] as string | number | boolean);
}

/**
 * One typed key in a template's parameter schema (§A3). The authoring UI renders a widget per
 * field via the FieldWidgetRegistry, grouped by `group`, filtered by `editableBy`, and shown only
 * when its `availableWhen` dependency (if any) is satisfied by the rest of the payload.
 */
export interface ParameterField {
  key: string;
  type: ParameterFieldType;
  options?: Array<string | number>;
  /** For a `currency` field, `min`/`max`/`default` are all in the field's minor unit (e.g. cents), not dollars. */
  min?: number | null;
  max?: number | null;
  default?: unknown;
  group?: string;
  label?: string;
  /** Human help text shown under the field label. */
  description?: string | null;
  /** Optional conceptual "learn more", shown via an info-icon tooltip beside the label. */
  tooltip?: string | null;
  editableBy?: ParameterEditableBy;
  availableWhen?: AvailableWhen | null;
}

/** The parameter schema a template registers, returned by `getTemplateSchema`. */
export interface ParameterSchema {
  template_key: string;
  parameters: ParameterField[];
}

export interface Experiment {
  id: string;
  key: string;
  name: string;
  description: string | null;
  owner: ExperimentOwner;
  owner_label: string;
  assignment_unit: string;
  assignment_unit_label: string;
  /**
   * Optional for the same transitional reason as the authoring schema's `optimizationModes`: a Core
   * that predates the field omits it from the detail GET too. Absent reads as `manual`.
   */
  optimization_mode?: OptimizationMode;
  optimization_mode_label?: string;
  status: ExperimentStatus;
  status_label: string;
  traffic_allocation: number;
  template_key: string | null;
  show_in_catalog: boolean;
  min_sample_size: number | null;
  min_runtime_hours: number | null;
  config_locked_at: string | null;
  is_locked: boolean;
  started_at: string | null;
  concluded_at: string | null;
  applies_to_all_shops: boolean;
  enrolled_shop_count?: number;
  variants?: ExperimentVariantSummary[];
  metrics?: ExperimentMetricSummary[];
  /**
   * Metrics the experiment's template now declares that its frozen metric snapshot lacks (ADR-0002
   * Amendment 3). Core sends it on the detail GET only, never on the list. It is an empty array when
   * there is no drift, and it can be non-empty only for a started, not-concluded experiment. Absent
   * means Core did not compute it (the list endpoint, or a Core that predates the field).
   */
  missing_template_metrics?: MissingTemplateMetric[];
}

/** A template metric absent from an experiment's snapshot. Core sends it without the snapshot row `id`. */
export type MissingTemplateMetric = Omit<ExperimentMetricSummary, 'id'>;

export interface MetricResult {
  metric_key: string;
  name: string;
  type: string;
  goal: string;
  unique_conversions: number;
  total_conversions: number;
  conversion_rate: number;
  /**
   * Optional for the same transitional reason as `optimization_mode` on `Experiment`: a Core that
   * predates these fields omits them. Absent reads as the plain unique-exposure denominator — the
   * fallback every call site uses. `null` is a distinct, real possibility handled separately
   * (denominatorResolution.ts's denominatorCountOf) from an omitted key: `denominator_source`
   * naming an override with `rate_denominator` explicitly `null` is a malformed pairing, excluded
   * rather than treated as the exposures fallback.
   */
  rate_denominator?: number | null;
  denominator_source?: string | null;
  /**
   * True when Core capped the numerator behind `conversion_rate` at `rate_denominator`.
   * `unique_conversions` keeps the raw count, so it can exceed `rate_denominator` while the rate reads at
   * most 100%: the denominator signal undercounts. Absent on a Core that predates the field, which reads
   * as false.
   */
  numerator_clamped?: boolean;
  /**
   * Set when the metric's template declares a denominator but this rate runs on exposures. `null`
   * when no fallback applies. Absent on a Core that predates the field. Typed `string`, not
   * {@link DenominatorFallbackReason}, because a newer Core can send a reason this app does not know.
   */
  denominator_fallback_reason?: string | null;
  uplift_vs_control: number | null;
  p_value: number | null;
  is_significant: boolean;
  posterior_mean?: number | null;
  ci_low?: number | null;
  ci_high?: number | null;
  prob_best?: number | null;
  prob_beats_control?: number | null;
  expected_loss?: number | null;
}

export interface VariantResult {
  variant_key: string;
  name: string;
  is_control: boolean;
  weight: number;
  unique_exposures: number;
  total_exposures: number;
  metrics: MetricResult[];
}

export interface ExperimentResults {
  experiment_key: string;
  scope: 'shop' | 'fleet';
  shop_id: number | null;
  primary_metric_key: string | null;
  total_unique_exposures: number;
  srm_healthy: boolean;
  srm_p_value: number | null;
  has_enough_data: boolean;
  leader_variant_key: string | null;
  variants: VariantResult[];
  readout?: ResultsReadout;
  scorecard_status?: ScorecardStatus | null;
  computed_at?: string | null;
  model_version?: string | null;
}

export interface VariantAssignment {
  experiment_key: string;
  variant_key: string;
  is_control: boolean;
  payload: Record<string, unknown>;
}

export interface OverviewRow {
  experiment_key: string;
  experiment_name: string;
  shop_id: number;
  enrollment_status: string;
  total_unique_exposures: number;
  srm_healthy: boolean;
  has_enough_data: boolean;
  leader_variant_key: string | null;
}

export function listExperiments (owner?: ExperimentOwner) {
  return apiClient.get<{ data: Experiment[] }>('/experiments', { params: owner ? { owner } : {} });
}

export function getExperiment (key: string) {
  return apiClient.get<{ data: Experiment }>(`/experiments/${key}`);
}

export function getResults (key: string, shopId?: number) {
  return apiClient.get<ExperimentResults | { data: ExperimentResults }>(`/experiments/${key}/results`, {
    params: shopId ? { shop_id: shopId } : {},
  });
}

export function getOverview (owner: ExperimentOwner = 'merchant') {
  return apiClient.get<{ data: OverviewRow[] }>('/experiments/overview', { params: { owner } });
}

// Initial shop enrollment is set on the create form (via `shop_ids` / `applies_to_all_shops` in the
// create payload). There is no post-create enrollment management: shops cannot be added to or removed
// from an experiment once it exists, so the per-experiment enroll/unenroll/apply-to-all endpoints are
// intentionally not wrapped here.

export function forceVariant (key: string, shopId: number, variant: string | null) {
  return apiClient.post<{ forced_variant: string | null }>(`/experiments/${key}/force-variant`, {
    shop_id: shopId,
    variant,
  });
}

export function startExperiment (key: string) {
  return apiClient.post<{ data: Experiment }>(`/experiments/${key}/start`);
}

export function pauseExperiment (key: string) {
  return apiClient.post<{ data: Experiment }>(`/experiments/${key}/pause`);
}

export function conclude (key: string, winningVariant: string | null) {
  return apiClient.post<{ data: Experiment }>(`/experiments/${key}/conclude`, { winning_variant: winningVariant });
}

export function reopen (key: string) {
  return apiClient.post<{ data: Experiment }>(`/experiments/${key}/reopen`);
}

/** Delete a draft experiment. Only never-started drafts are deletable (409 otherwise). */
export function deleteExperiment (key: string) {
  return apiClient.delete<{ deleted: boolean }>(`/experiments/${key}`);
}

/** The template key whose authoring form collects Checkout+ baseline inputs and shows test planning. */
export const CHECKOUT_PLUS_CONVERSION_TEMPLATE_KEY = 'checkout_plus_conversion_rate';

/**
 * Per-shop baseline inputs for a Checkout+ conversion test — the fields a Loop teammate used to type
 * into the "Return Coverage Experimentation — Shop Inputs" Google Sheet. The merchant supplies the
 * last-30-day numbers; the four `avg_daily_*` counts are whole sessions/orders per day, the two
 * `baseline_*_rate` fields are decimals in [0, 1]. They drive the in-form sample-size / duration
 * estimate and travel with the create/update payload so Core becomes the system of record.
 */
export interface ExperimentBaselines {
  avg_daily_sessions: number | null;
  avg_daily_checkout_start_sessions: number | null;
  avg_daily_cart_views_sessions: number | null;
  avg_daily_ecommerce_orders: number | null;
  /** Completed checkouts ÷ checkout starts, as a decimal in [0, 1]. */
  baseline_checkout_conversion_rate: number | null;
  /** Carts that did not convert, as a decimal in [0, 1]. */
  baseline_cart_abandonment_rate: number | null;
  /** Whether the merchant already charged a coverage fee before this test (sheet's indicator). */
  previously_charged_fees: boolean;
  /** Which test this is for the shop (1 for the first, 2 for a re-run, …). */
  test_number: number | null;
  /**
   * Derived at submit from the baselines — not user-entered. Which statistical method the planning
   * heuristic recommends for reading this fixed A/B test's results (same vocabulary as
   * {@link ResultsReadout}). Recorded so the test type stays traceable; deliberately not shown in the
   * authoring UI. `null` when the inputs are incomplete. Unrelated to the `ml_optimized` bandit.
   */
  recommended_analysis_method?: ResultsReadout | null;
}

export interface CreateExperimentVariant {
  key: string;
  name: string;
  is_control: boolean;
  weight: number;
  payload: Record<string, unknown>;
}

export interface CreateExperimentMetric {
  key: string;
  name: string;
  type: MetricType;
  goal: MetricGoal;
}

export interface CreateExperimentPayload {
  name: string;
  description?: string | null;
  owner: ExperimentOwner;
  /** Defaults to `shopper` per the Phase 1 kickoff. */
  assignment_unit?: string;
  /** How the per-segment split is set: `manual` (fixed A/B) or `ml_optimized`. Defaults to `manual`. */
  optimization_mode?: OptimizationMode;
  template_key: string | null;
  /**
   * The Surface (area) this experiment runs on. Sent only when no template is selected — a template
   * derives its Surface in Core, which rejects a conflicting explicit one. `null` means a deliberate
   * ad-hoc experiment (no area); the key is omitted entirely on the template path.
   */
  surface?: string | null;
  /** Basis points, 0–10000 (10000 = 100%). */
  traffic_allocation: number;
  min_sample_size?: number | null;
  min_runtime_hours?: number | null;
  variants: CreateExperimentVariant[];
  metrics: CreateExperimentMetric[];
  /**
   * Per-shop baseline inputs. Sent only for the Checkout+ conversion template, where the authoring
   * form collects them; omitted for every other template.
   */
  baselines?: ExperimentBaselines | null;
  /** Optional initial enrollment. Merchant → exactly one shop; platform → any, or use apply-to-all. */
  shop_ids?: number[];
  applies_to_all_shops?: boolean;
}

/** A variant patch usable while the experiment is a draft: add, edit, or (omit to keep) an arm. */
export interface UpdateExperimentVariant {
  key: string;
  name?: string;
  is_control?: boolean;
  weight?: number;
  payload?: Record<string, unknown> | null;
}

export interface UpdateExperimentMetric {
  key: string;
  name?: string;
  type?: MetricType;
  goal?: MetricGoal;
}

export interface UpdateExperimentPayload {
  name?: string;
  description?: string | null;
  traffic_allocation?: number;
  min_sample_size?: number | null;
  min_runtime_hours?: number | null;
  template_key?: string | null;
  show_in_catalog?: boolean;
  /**
   * Weight-only edits on a running experiment, or the full draft arm set (add/remove/edit,
   * including `payload`) while `status=draft`.
   */
  variants?: UpdateExperimentVariant[];
  /** Draft-only: the full metric set to persist (add/remove/edit). */
  metrics?: UpdateExperimentMetric[];
  /** Per-shop baseline inputs for the Checkout+ conversion template. */
  baselines?: ExperimentBaselines | null;
}

export function createExperiment (payload: CreateExperimentPayload) {
  return apiClient.post<{ data: Experiment }>('/experiments', payload);
}

export function updateExperiment (key: string, payload: UpdateExperimentPayload) {
  return apiClient.put<{ data: Experiment }>(`/experiments/${key}`, payload);
}

export function getTemplateSchema (template: string) {
  return apiClient.get<{ data: ParameterSchema }>(`/experiments/experiment-templates/${template}/schema`);
}

export interface OwnerOption {
  value: ExperimentOwner;
  label: string;
}

export interface OptimizationModeOption {
  value: OptimizationMode;
  label: string;
}

export interface AssignmentUnitOption {
  value: string;
  label: string;
  /** Null = always selectable; otherwise only when the named field (e.g. `owner`) matches. */
  availableWhen: AvailableWhen | null;
}

/** A canonical metric a template declares it measures — keys match what the template's edge emits. */
export interface TemplateMetric {
  key: string;
  name: string;
  type: MetricType;
  goal: MetricGoal;
  description?: string | null;
}

/** A canonical arm a template owns — seeds the authoring UI's arm list with its fixed payload. */
export interface TemplateFixedArm {
  key: string;
  name: string;
  is_control: boolean;
  payload: Record<string, unknown>;
}

/**
 * One selectable Surface (area) in the authoring picker. A Surface is also the mutual-exclusion layer
 * — a merchant runs at most one experiment per Surface — so choosing one decides what this experiment
 * competes with. Backed by Core's governed `Surface` enum.
 */
export interface SurfaceOption {
  value: string;
  label: string;
  /** One-line blurb explaining the area, shown under the picker. */
  description: string;
}

/** One selectable template in the authoring dropdown. */
export interface ExperimentTemplateOption {
  value: string;
  label: string;
  /** The Surface this template runs on. A template experiment derives it — it isn't author-chosen. */
  surface?: string | null;
  /**
   * How arms are authored — the sole signal the UI branches on: `schema` form, `freeform` JSON, or
   * `none` (no payload UI). Backed by Core's `PayloadMode` enum, whose string values these mirror.
   */
  payloadMode: 'none' | 'freeform' | 'schema';
  /** Optional purpose blurb shown under the dropdown. */
  description?: string | null;
  /** Canonical metrics this template measures; the authoring UI pre-populates them (keys locked). */
  metrics: TemplateMetric[];
  /**
   * Arms the template owns (e.g. control with the offer hidden vs treatment with it shown), each with
   * its own payload, or `[]`/absent when arms are freely authored. Seeds the authoring UI's arm list
   * so each arm starts from its real payload — users may still edit or add arms.
   */
  fixedArms?: TemplateFixedArm[];
}

/** The experiment-level field schema (owner, owner-gated units, Surface + template catalogs) for the authoring UI. */
export interface AuthoringSchema {
  owners: OwnerOption[];
  assignmentUnits: AssignmentUnitOption[];
  /**
   * How the traffic split is set (manual vs ML-optimized). Optional for the same transitional reason
   * as `surfaces`: a Core that predates the field omits it and the UI falls back to a built-in list.
   */
  optimizationModes?: OptimizationModeOption[];
  /**
   * The governed Surface catalog a template-less experiment picks from. Optional: a Core that predates
   * the catalog omits it, and the authoring page falls back to an empty list (the picker then offers
   * only "None"), so the type matches that transitional contract rather than promising the field.
   */
  surfaces?: SurfaceOption[];
  templates: ExperimentTemplateOption[];
}

export function getAuthoringSchema () {
  return apiClient.get<{ data: AuthoringSchema }>('/experiments/authoring-schema');
}
